import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Link,
  useParams,
} from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';

import { APIpasswordChange } from '../api/Auth';

const PasswordChange: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const { token } = useParams({ strict: false });

  const { mutate, isPending, isError } = useMutation({
    mutationFn: async (data: { password: string; token: string }) => {
      const response = await APIpasswordChange(data.token, data.password);
      return response;
    },
    onError: (error) => {
      setErrorMessage(error.message || 'An error occurred.');
    },
    onSuccess: () => {
      setErrorMessage(null);
      setSuccess(true);
    },
  });

  const schema = z.object({
    password: z.string({ message: "Password is required" }).min(8, { message: "Password must be at least 8 characters" }),
    password_confirm: z.string({ message: "Password confirmation is required" }),
  }).refine((data) => data.password === data.password_confirm, {
    message: "Passwords do not match",
    path: ['password_confirm'],
  });

  const { register, handleSubmit, formState: { errors } } = useForm<{ password: string; password_confirm: string }>({
    resolver: zodResolver(schema) as any,
  });

  const onSubmit = (data: { password: string }) => {
    mutate({ token, password: data.password });
  };

  return (
    <div className="container password-reset-container">
      <div className="wa-grid wa-gap-0">
        <div className="password-reset-form">
          {isError && (
            <wa-callout variant="danger">
              <i className="fa-regular fa-circle-exclamation" slot="icon"></i>
              <strong>{errorMessage}</strong>
            </wa-callout>
          )}
          {success ? (
            <>
              <wa-callout variant="success">
                <i className="fa-regular fa-circle-check" slot="icon"></i>
                <strong>Password changed successfully!</strong>
                <p>Your password has been updated. You can now use your new password to log in.</p>
              </wa-callout>
              <div className="password-reset-info">
                <Link to="/login">
                  <wa-button>
                    Login
                  </wa-button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <wa-input type="password" label="New Password" required className={`${errors.password ? 'invalid' : ''}`} autofocus {...register('password')} placeholder="Enter your new password" hint={errors.password?.message}></wa-input>
                <wa-input type="password" label="Confirm New Password" required className={`${errors.password_confirm ? 'invalid' : ''}`} autofocus {...register('password_confirm')} placeholder="Confirm your new password" hint={errors.password_confirm?.message}></wa-input>
                <div className="center">
                  <wa-button type="submit" disabled={isPending}>
                    {isPending ? 'Changing...' : 'Change password'}
                  </wa-button>
                </div>
              </form>
              <div className="password-reset-info">
                Don't have an account? <Link to="/signup">Sign up here</Link>.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PasswordChange;
